import React, { useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { SafeAreaView } from 'react-native-safe-area-context'
import { TouchableOpacity, View } from 'react-native'
import { useFocusEffect, useNavigation } from '@react-navigation/native'
import moment from 'moment'
import { ErrorMessages } from '@src/config'
import { useDispatchAsync, useSnack } from '@src/utils'
import {
  MONTH_DAY_YEAR_TIME_TWELVE_HOUR_FORMAT,
  SHORT_MONTH_AND_DATE_WITH_LONG_YEAR_FORMAT,
} from '@src/config/momentFormat'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Icon, Text } from '@src/components/base'
import { LabResult, LabResultProcessing } from '@src/types'
import { LoadingIndicator, ScrollViewWithFade } from '@src/components'
import { useSecureScreen } from '@src/hooks/useSecureScreen'
import { labResultsProcessingSelector, labResultsSelector } from '../models/labResults.selectors'
import { NoLabResultsProvidedComponent } from '../components/NoLabResultsProvidedComponent'
import { LabResultCard } from '../components/LabResultCard'
import { LabResultsNavigationContainer } from '../components/LabResultsNavigationContainer'
import { LabResultProcessingCard } from '../components/LabResultProcessingCard'
import {
  useAcknowledgeLabResultAnalysisComplete,
  useAcknowledgeLabResultAnalysisFailed,
} from '../hooks'

export const LabResultsScreen = () => {
  useSecureScreen()

  const styles = useStyleSheet(themedStyles)
  const labResults = useSelector(labResultsSelector)
  const labResultsProcessing = useSelector(labResultsProcessingSelector)
  const dispatchAsync = useDispatchAsync()
  const showSnack = useSnack()
  const [fetchingLabResults, setFetchingLabResults] = useState(false)
  const isFetchingRef = useRef(false)
  const acknowledgeLabResultFailed = useAcknowledgeLabResultAnalysisFailed()
  const acknowledgeLabResultAnalysisComplete = useAcknowledgeLabResultAnalysisComplete()
  const navigation = useNavigation()

  const markLabResultsAsViewed = useCallback(() => {
    acknowledgeLabResultFailed()
    acknowledgeLabResultAnalysisComplete()
  }, [acknowledgeLabResultAnalysisComplete, acknowledgeLabResultFailed])

  const fetchLabResults = useCallback(async () => {
    if (isFetchingRef.current) {
      return
    }

    isFetchingRef.current = true
    setFetchingLabResults(true)

    try {
      await dispatchAsync({
        type: 'labResults/fetchLabResults',
        complete: markLabResultsAsViewed,
      })
    } catch (error: any) {
      const errorMessage = error?.message || ErrorMessages.ServerError
      showSnack(errorMessage, null, 'error')
    } finally {
      isFetchingRef.current = false
      setFetchingLabResults(false)
    }
  }, [dispatchAsync, markLabResultsAsViewed, showSnack])

  useFocusEffect(
    useCallback(() => {
      fetchLabResults()

      return () => {
        setFetchingLabResults(false)
        isFetchingRef.current = false
      }
    }, [fetchLabResults]),
  )

  if (labResults.length === 0 && labResultsProcessing.length === 0) {
    return (
      <LabResultsNavigationContainer screen="LabResultsScreen">
        <NoLabResultsProvidedComponent />
      </LabResultsNavigationContainer>
    )
  }

  return (
    <LabResultsNavigationContainer screen="LabResultsScreen">
      <SafeAreaView style={styles.container} edges={['bottom']}>
        <ScrollViewWithFade>
          {labResults.length > 0 && (
            <>
              <View style={styles.sectionHeader}>
                <Text bold type="large">
                  Ready to View
                </Text>
              </View>
              {[...labResults]
                .sort((a, b) => {
                  // sort by lab result date
                  const dateA = Date.parse(a.date)
                  const dateB = Date.parse(b.date)

                  if (dateB !== dateA) {
                    return dateB - dateA
                  }

                  // If the dates are the same, sort by createdAt to ensure consistent order
                  const createdAtA = Date.parse(a.createdAt)
                  const createdAtB = Date.parse(b.createdAt)

                  return createdAtB - createdAtA
                })
                .map((labResult: LabResult) => (
                  <View key={labResult.id} style={styles.labResultCard}>
                    <LabResultCard
                      date={moment(labResult.date).format(
                        SHORT_MONTH_AND_DATE_WITH_LONG_YEAR_FORMAT,
                      )}
                      createdAt={moment(labResult.createdAt).format(
                        MONTH_DAY_YEAR_TIME_TWELVE_HOUR_FORMAT,
                      )}
                      id={labResult.id}
                    />
                  </View>
                ))}
            </>
          )}
          {labResultsProcessing.length > 0 && (
            <>
              <View style={styles.sectionHeader}>
                <Text bold type="large">
                  Processing
                </Text>
                <TouchableOpacity
                  onPress={fetchLabResults}
                  accessibilityLabel="Refresh lab results"
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  {fetchingLabResults ? (
                    <LoadingIndicator size="small" />
                  ) : (
                    <Icon name="arrows-clockwise" size={24} style={styles.refreshIcon} />
                  )}
                </TouchableOpacity>
              </View>
              {labResultsProcessing.map((labResult: LabResultProcessing) => (
                <View key={labResult.id} style={styles.labResultCard}>
                  <LabResultProcessingCard
                    key={labResult.id}
                    uploadedAt={moment(labResult.createdAt).format(
                      MONTH_DAY_YEAR_TIME_TWELVE_HOUR_FORMAT,
                    )}
                  />
                </View>
              ))}
            </>
          )}
        </ScrollViewWithFade>
        <Button
          accessibilityLabel="Upload new lab results"
          onPress={() => navigation.navigate('AddLabResults')}
          style={styles.uploadButton}
          size="block"
          type="primary"
        >
          Add Lab Results
        </Button>
      </SafeAreaView>
    </LabResultsNavigationContainer>
  )
}

const themedStyles = StyleService.create({
  container: {
    flex: 1,
    paddingTop: 8,
    paddingHorizontal: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  refreshIcon: {
    marginRight: 4,
  },
  uploadButton: {
    marginBottom: 8,
  },
  labResultCard: {
    marginBottom: 8,
  },
})
